import {
    ApplicationConfig,
    ErrorHandler,
    importProvidersFrom,
    inject,
    isDevMode,
    provideAppInitializer
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
    provideRouter,
    withEnabledBlockingInitialNavigation,
    withInMemoryScrolling,
    withRouterConfig,
    withViewTransitions
} from '@angular/router';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalInterceptor,
    MsalRedirectComponent,
    MsalService
} from '@azure/msal-angular';
import { DropdownModule, SidebarModule } from '@coreui/angular-pro';
import { IconSetService } from '@coreui/icons-angular';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { InteractionStatus } from '@azure/msal-browser';
import { catchError, filter, firstValueFrom, map, Observable, of, switchMap, take } from 'rxjs';
import { routes } from './app.routes';
import { TranslocoHttpLoader } from '@core/i18n/transloco-http-loader';
import { MSALInstanceFactory } from '@core/auth/msal-instance.factory';
import { MSALInterceptorConfigFactory } from '@core/auth/msal-interceptor-config.factory';
import { MSALGuardConfigFactory } from '@core/auth/msal-guard-config.factory';
import { GlobalErrorHandler } from '@core/services/global-error-handler.service';
import { HttpErrorInterceptor } from '@core/interceptors/http-error.interceptor';
import { UiCultureInterceptor } from '@core/interceptors/ui-culture.interceptor';
import { USER_SERVICE } from '@core/services/user.service.interface';
import { UserDevService } from '@core/services/user.dev.service';
import { UserService } from '@core/services/user.service';
import { UserProfileService } from '@core/services/user-profile.service';
import { FeatureFlagService } from '@core/services/feature-flag.service';
import { UiLanguage, UiLanguages, UI_LANGUAGE_STORAGE_KEY } from '@core/constants/ui-languages';
import { iconSubset } from './icons/icon-subset';

function resolveInitialLanguage(availableLangs: UiLanguage[]): UiLanguage {
    const storedLanguage = localStorage.getItem(UI_LANGUAGE_STORAGE_KEY) as UiLanguage | null;
    if (storedLanguage && availableLangs.includes(storedLanguage)) {
        return storedLanguage;
    }

    const browserLanguage = navigator.language?.split('-')[0] as UiLanguage;
    if (browserLanguage && availableLangs.includes(browserLanguage)) {
        return browserLanguage;
    }

    return UiLanguages.English;
}

function resolveBackendLanguage(availableLangs: UiLanguage[]): Observable<UiLanguage | null> {
    if (isDevMode()) {
        return of(null);
    }

    const userProfileService = inject(UserProfileService);

    return inject(MsalBroadcastService).inProgress$.pipe(
        filter(status => status === InteractionStatus.None),
        take(1),
        switchMap(() => userProfileService.getLanguage()),
        map(language => (availableLangs.includes(language) ? language : null)),
        catchError(() => of(null))
    );
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(
            routes,
            withRouterConfig({
                onSameUrlNavigation: 'reload'
            }),
            withInMemoryScrolling({
                scrollPositionRestoration: 'top',
                anchorScrolling: 'enabled'
            }),
            withEnabledBlockingInitialNavigation(),
            withViewTransitions()
        ),
        importProvidersFrom(SidebarModule, DropdownModule),
        IconSetService,
        provideTransloco({
            config: {
                availableLangs: [UiLanguages.English, UiLanguages.Polish, UiLanguages.Spanish],
                defaultLang: UiLanguages.English,
                fallbackLang: UiLanguages.English,
                reRenderOnLangChange: true,
                prodMode: !isDevMode()
            },
            loader: TranslocoHttpLoader
        }),
        provideAnimationsAsync(),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            const availableLangs = translocoService
                .getAvailableLangs()
                .map(lang => (typeof lang === 'string' ? lang : lang.id)) as UiLanguage[];
            const fallbackLanguage = resolveInitialLanguage(availableLangs);

            return firstValueFrom(resolveBackendLanguage(availableLangs)).then(backendLanguage => {
                const initialLanguage = backendLanguage ?? fallbackLanguage;
                translocoService.setActiveLang(initialLanguage);
                return firstValueFrom(translocoService.load(initialLanguage));
            });
        }),
        provideAppInitializer(() => firstValueFrom(inject(FeatureFlagService).load())),
        provideAppInitializer(() => {
            inject(IconSetService).icons = { ...iconSubset };
        }),
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        MsalRedirectComponent,
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: UiCultureInterceptor,
            multi: true
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        UserDevService,
        {
            provide: USER_SERVICE,
            useFactory: (devService: UserDevService, prodService: UserService) => {
                return isDevMode() ? devService : prodService;
            },
            deps: [UserDevService, UserService]
        }
    ]
};
